import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { FileSpreadsheet, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function SpreadsheetsPage() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    api.get("/spreadsheets").then((r) => { setSheets(r.data); setLoading(false); });
  }, []);

  const open = async (id) => {
    setOpenId(id);
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/spreadsheets/${id}/preview`);
      setPreview(data);
    } finally { setPreviewLoading(false); }
  };

  return (
    <AppLayout title="Spreadsheets" subtitle="Excel files across your SharePoint sites">
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (<div key={i} className="h-24 bg-white border border-slate-200 rounded-lg shimmer" />))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                {["File", "Site", "Rows", "Size", "Modified", ""].map((c) => (
                  <th key={c} className="text-left font-medium px-5 py-2.5">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sheets.map((s) => (
                <tr key={s.id} data-testid={TID.spreadsheetRow(s.id)} className="border-b border-slate-100 hover:bg-slate-50/70">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <FileSpreadsheet className="w-4 h-4 text-emerald-700" />
                      <span className="text-slate-800 font-medium">{s.name}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 ml-6">{s.author}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full border border-slate-200 text-slate-600">{s.site}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-600 tabular-nums">{s.rows}</td>
                  <td className="px-5 py-3 text-slate-600 tabular-nums">{s.size}</td>
                  <td className="px-5 py-3 text-slate-600 tabular-nums">{s.modified}</td>
                  <td className="px-5 py-3">
                    <Button
                      data-testid={TID.spreadsheetOpen(s.id)}
                      onClick={() => open(s.id)}
                      variant="outline"
                      size="sm"
                      className="h-8"
                    >
                      Preview <ExternalLink className="w-3 h-3 ml-1.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) { setOpenId(null); setPreview(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display">{preview?.meta?.name || "Preview"}</DialogTitle>
            <DialogDescription>
              {preview?.meta ? `${preview.meta.site} · ${preview.meta.rows} rows · ${preview.meta.size}` : "Loading preview…"}
            </DialogDescription>
          </DialogHeader>
          {previewLoading || !preview ? (
            <div className="h-64 rounded-md shimmer" />
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    {preview.preview.columns.map((c) => (
                      <th key={c} className="text-left font-medium text-[11px] uppercase tracking-wider text-slate-500 px-4 py-2">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      {r.map((cell, j) => (
                        <td key={j} className="px-4 py-2 text-slate-700 tabular-nums">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-xs text-slate-500">
            First rows only. Full file lives in SharePoint.
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
